import { cn } from "@util/helpers";

type DropdownSelectorListProps = {
  /** The filtered options */
  filteredOptions: { value: string; label: string }[];
  /** The function to select an option */
  handleSelectOption: (option: { value: string; label: string }) => void;
  /** Set a ref for a list item */
  setItemEl: (index: number, el: HTMLLIElement | null) => void;
  /** The active index */
  activeIndex: number;
  /** The class name of the list */
  className?: string;
  /** The listbox id */
  id?: string;
  /** Base id for options */
  optionIdBase?: string;
  /** The selected value */
  selectedValue?: string;
  /** The variant class name for the dropdown list */
  variantClassName?: {
    container?: string;
    item?: string;
    selectedBg?: string;
    selected?: string;
  };
  /** Whether to display the search input in the dropdown */
  isDisplaySearch?: boolean;
};

/** The list of options to display in the dropdown */
const DropdownSelectorList = ({
  filteredOptions,
  handleSelectOption,
  setItemEl,
  activeIndex,
  id,
  optionIdBase,
  variantClassName,
  selectedValue,
  isDisplaySearch,
}: DropdownSelectorListProps) => {
  const className = cn(
    "max-h-[250px] overflow-y-auto",
    {
      "max-h-[calc(250px-56px)] overflow-y-auto": isDisplaySearch,
    },
    variantClassName?.container
  );

  return (
    <ul
      id={id}
      className={className}
      role="listbox"
      data-testid="dropdown-list-component"
    >
      {filteredOptions.map((option, index) => (
        <li
          key={option.value}
          id={optionIdBase ? `${optionIdBase}-${index}` : undefined}
          role="option"
          aria-selected={index === activeIndex}
          data-name={option.value}
          onClick={() => {
            handleSelectOption(option);
          }}
          ref={(el) => {
            setItemEl(index, el);
          }}
          className={cn(
            "cursor-pointer border-b border-gray-175 px-3 py-3 font-poppins text-sm leading-150 tracking-2 text-gray-850",
            variantClassName?.item,
            {
              [variantClassName?.selectedBg || "bg-gray-250"]:
                index === activeIndex,
              [variantClassName?.selected || "text-blue-175"]:
                selectedValue === option.value,
            }
          )}
        >
          {option.label}
        </li>
      ))}
    </ul>
  );
};

export default DropdownSelectorList;
